export type MatchStatus = 'live' | 'upcoming' | 'completed';

export interface PublicMatch {
  id: string;
  matchId: string | null;
  courtId: string;
  scheduledAt: string;
  durationEstimateMin: number;
  stage: string;
  division: string | null;
  players: string[];
  /** Optional for schedule payloads cached before event player navigation. */
  playerIds?: (string | null)[];
  divisionId?: string | null;
  /** Team-division roster, verbatim, aligned index-for-index with `players`. */
  rosters?: (string | null)[];
  score: { p1: number; p2: number } | null;
  /** Current-game points while `status === 'live'`; null/absent otherwise. */
  liveGame?: { p1: number; p2: number } | null;
  /** Per-game points in play order (incl. the current game while live); absent = unknown. */
  games?: { p1: number; p2: number; partial?: boolean }[];
  /** Draw match code, e.g. "QUAL-B2"; null/absent when not from a draw. */
  code?: string | null;
  /** Seeding group per player, aligned with `players`; null when unseeded. */
  seeds?: (string | null)[];
  isWalkover: boolean;
  outcome?: string | null;
  /** The linked match's winner (`match.winner_id`); a retirement's winner is never the games leader. */
  winnerId?: string | null;
  /** The player who retired; null/absent unless outcome is 'retired'. */
  retiredPlayerId?: string | null;
  status: MatchStatus;
}

export interface CourtState {
  id: string;
  name: string;
  label?: string;
  delayMin: number;
  liveMatchId: string | null;
}

export function effectiveStartIso(match: PublicMatch, courtDelayMin: number): string {
  const base = new Date(match.scheduledAt).getTime();
  return new Date(base + courtDelayMin * 60_000).toISOString();
}

export function matchStatusAt(match: PublicMatch, courtDelayMin: number, now: Date): MatchStatus {
  if (match.status === 'live' || match.status === 'completed') return match.status;
  const startMs = new Date(effectiveStartIso(match, courtDelayMin)).getTime();
  const endMs = startMs + match.durationEstimateMin * 60_000;
  if (now.getTime() >= endMs) return 'completed';
  if (now.getTime() >= startMs) return 'live';
  return 'upcoming';
}

// `accentHex` is a raw hex for JS-side consumers (canvas/SVG/chart libs) that can't use Tailwind classes. CSS callers should use bg/text/border.
interface ColorPalette {
  bg: string;
  text: string;
  border: string;
  accentHex: string;
}

const PALETTE: Record<MatchStatus, ColorPalette> = {
  completed: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-900',
    border: 'border-emerald-400',
    accentHex: '#34d399',
  },
  live: {
    bg: 'bg-orange-50',
    text: 'text-orange-900',
    border: 'border-orange-400',
    accentHex: '#fb923c',
  },
  upcoming: {
    bg: 'bg-gray-50',
    text: 'text-gray-900',
    border: 'border-gray-200',
    accentHex: '#e5e7eb',
  },
};

export function statusColors(status: MatchStatus): ColorPalette {
  return PALETTE[status];
}

export function formatRelativeStart(
  iso: string,
  delayMin: number,
  locale: string,
  timeZone?: string,
): string {
  const formatted = new Date(iso).toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  });
  return delayMin > 0 ? `~${formatted}` : formatted;
}

// `timeZone` mirrors the `(iso, locale, timeZone?)` signature shared with
// `LocalDateTime`/`utils.ts` formatters: pass `'UTC'` before mount so SSR and
// the first client render agree, then `undefined` (browser-local) after mount.
// Calling this directly in a client render without that gate is what threw
// React #418 on the schedule pages.
export function formatScheduledTime(iso: string, locale: string, timeZone?: string): string {
  return new Date(iso).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone,
  });
}

/**
 * Extract the YYYY-MM-DD date portion of a stored ISO timestamp without
 * going through `new Date()` — avoids the UTC-vs-local-tz trap on the
 * Node server. Edge case: a match scheduled before ~08:00 in a UTC+8 zone
 * is stored as the previous UTC day and will bucket "wrong" by this rule
 * (needs event.timezone for a complete fix).
 */
export function dayFromIso(iso: string): string {
  return iso.slice(0, 10);
}
